// src/components/groups/GroupDetail.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  useGetGroupByIdQuery,
  useGetGroupStatisticsQuery,
  useRemoveDevicesFromGroupMutation,
  useRemoveManagerFromGroupMutation,
  useRemoveViewerFromGroupMutation,
} from "../../redux/admin/groupApi";
import { toast } from "react-hot-toast";

import AddViewerModal from "./AddViewerModal";
import AddDeviceToGroupModal from "./AddDeviceToGroupModal";
import AddManagerModal from "./AddManagerModal";

import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Avatar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Skeleton,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  isDemoLocalId,
  readDemoLocalItem,
} from "../../utils/demoLocalCrud";

// ✅ MUI DataGrid
import { DataGrid } from "@mui/x-data-grid";

import {
  Users,
  Smartphone,
  Plus,
  ArrowLeft,
  Trash2,
  BarChart3,
  ShieldCheck,
  Eye,
  Edit2,
  RefreshCw,
  MapPin,
  Search,
  SlidersHorizontal,
  CalendarDays,
} from "lucide-react";

const StatCard = ({ label, value, subValue, icon, color }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.2 },
        minHeight: 152,
        borderRadius: "12px",
        border: "1px solid rgba(226,232,240,0.95)",
        boxShadow: "0 18px 44px rgba(15, 23, 42, 0.06)",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.75,
        background: "#fff",
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "12px",
          display: "grid",
          placeItems: "center",
          background: alpha(color || "#6366f1", 0.12),
          color: color || "#6366f1",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ color: "#0f172a", fontSize: 13, fontWeight: 900, mb: 1.3 }}>
          {label}
        </Typography>
        <Typography sx={{ color: "#0f172a", fontSize: 30, lineHeight: 1, fontWeight: 950 }}>
          {value}
        </Typography>
        {subValue ? (
          <Typography sx={{ display: "block", mt: 1.8, color: "#64748b", fontSize: 12, fontWeight: 700 }}>
            {subValue}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
};

const ConfirmDialog = ({ open, title, desc, loading, onClose, onConfirm }) => (
  <Dialog open={open} onClose={loading ? () => {} : onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>{title}</DialogTitle>
    <DialogContent>
      <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>{desc}</Typography>
    </DialogContent>
    <DialogActions sx={{ p: 2.2, gap: 1 }}>
      <Button onClick={onClose} disabled={loading} variant="outlined" sx={{ borderRadius: 2.5, fontWeight: 800, minWidth: 110 }}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        disabled={loading}
        variant="contained"
        color="error"
        startIcon={loading ? <CircularProgress size={16} sx={{ color: "white" }} /> : <Trash2 size={16} />}
        sx={{ borderRadius: 2.5, fontWeight: 900, minWidth: 110 }}
      >
        Remove
      </Button>
    </DialogActions>
  </Dialog>
);

export default function GroupDetail() {
  const { id: groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"));

  // modals
  const [isViewerModalOpen, setIsViewerModalOpen] = useState(false);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState("");

  // confirm state
  const [confirm, setConfirm] = useState({ open: false, type: null, payload: null });

  // queries
  const isDemoLocalGroup = isDemoLocalId(groupId, "groups");
  const stateGroup =
    isDemoLocalGroup &&
    String(location.state?.group?._id || location.state?.group?.id || "") ===
      String(groupId || "")
      ? location.state.group
      : null;
  const localGroup = useMemo(
    () => stateGroup || (isDemoLocalGroup ? readDemoLocalItem("groups", groupId) : null),
    [groupId, isDemoLocalGroup, stateGroup]
  );
  const { data: groupData, isLoading, error, refetch } = useGetGroupByIdQuery(
    groupId,
    { skip: isDemoLocalGroup }
  );
  const { data: statsData, refetch: refetchStats } = useGetGroupStatisticsQuery(
    groupId,
    { skip: isDemoLocalGroup }
  );

  // mutations
  const [removeDevice, { isLoading: isRemovingDevice }] = useRemoveDevicesFromGroupMutation();
  const [removeManager, { isLoading: isRemovingManager }] = useRemoveManagerFromGroupMutation();
  const [removeViewer, { isLoading: isRemovingViewer }] = useRemoveViewerFromGroupMutation();

  const group = localGroup || groupData?.data;
  const stats =
    statsData?.data ||
    (localGroup
      ? {
          devices: {
            total: Array.isArray(localGroup?.devices) ? localGroup.devices.length : 0,
            online: 0,
            idle: 0,
            moving: 0,
          },
        }
      : null);

  const headerColor = group?.color || "#3b82f6";

  const refreshGroupDetail = () => {
    if (!isDemoLocalGroup) {
      refetch();
      refetchStats();
    }
  };

  const handleRefresh = () => {
    refreshGroupDetail();
    toast.success("Data refreshed");
  };

  const openRemove = (type, payload) => setConfirm({ open: true, type, payload });
  const closeRemove = () => setConfirm({ open: false, type: null, payload: null });

  const confirmLoading =
    (confirm.type === "device" && isRemovingDevice) ||
    (confirm.type === "manager" && isRemovingManager) ||
    (confirm.type === "viewer" && isRemovingViewer);

  const handleConfirmRemove = async () => {
    try {
      if (confirm.type === "device") {
        const device = confirm.payload;
        await removeDevice({ groupId, deviceIds: [device._id] }).unwrap();
        toast.success("Device removed successfully");
        closeRemove();
        refreshGroupDetail();
      }
      if (confirm.type === "manager") {
        const userId = confirm.payload;
        await removeManager({ groupId, userId }).unwrap();
        toast.success("Manager removed successfully");
        closeRemove();
        refreshGroupDetail();
      }
      if (confirm.type === "viewer") {
        const userId = confirm.payload;
        await removeViewer({ groupId, userId }).unwrap();
        toast.success("Viewer removed successfully");
        closeRemove();
        refreshGroupDetail();
      }
    } catch (err) {
      toast.error(err?.data?.msg || "Remove failed");
    }
  };

  const deviceRows = useMemo(() => {
    const list = group?.devices || [];
    return list.map((d) => ({
      id: d._id,
      name: d.name || "Unnamed",
      imei: d.imei || "—",
      status: d.status || "offline",
      speed: d.liveData?.speed ?? 0,
      lastUpdate: d.liveData?.lastUpdate || d.updatedAt || d.lastUpdate || "",
      model: d.model || "",
      raw: d,
    }));
  }, [group]);

  const filteredDeviceRows = useMemo(() => {
    const query = deviceSearch.trim().toLowerCase();
    if (!query) return deviceRows;
    return deviceRows.filter((device) =>
      [device.name, device.imei, device.model]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [deviceRows, deviceSearch]);

  const onlineCount = Number(stats?.devices?.online || 0);
  const idleCount = Number(stats?.devices?.idle || 0);
  const offlineCount = Math.max(0, Number(stats?.devices?.total || group?.devices?.length || 0) - onlineCount - idleCount);
  const deviceStatusTotal = Math.max(1, onlineCount + idleCount + offlineCount);

  const deviceCols = useMemo(
    () => [
      {
        field: "name",
        headerName: "Device",
        flex: 1,
        minWidth: 220,
        renderCell: (p) => {
          const online = p.row.status === "online";
          return (
            <Stack
              onClick={() => navigate(`/devices/${p.row.id}`)}
              sx={{ cursor: "pointer" }}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 99,
                  background: online ? theme.palette.success.main : theme.palette.text.disabled,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={900} noWrap>
                  {p.row.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                  {p.row.model || "—"}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
      { field: "imei", headerName: "IMEI", flex: 1, minWidth: 180 },
      {
        field: "status",
        headerName: "Status",
        minWidth: 130,
        renderCell: (p) => (
          <Chip
            size="small"
            label={p.value === "online" ? "Online" : "Offline"}
            color={p.value === "online" ? "success" : "default"}
            variant={p.value === "online" ? "filled" : "outlined"}
            sx={{ borderRadius: 2, fontWeight: 800 }}
          />
        ),
      },
      {
        field: "speed",
        headerName: "Speed",
        minWidth: 130,
        renderCell: (p) =>
          p.value > 0 ? (
            <Stack direction="row" spacing={0.6} alignItems="center">
              <MapPin size={14} />
              <Typography fontWeight={800}>{p.value} km/h</Typography>
            </Stack>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>—</Typography>
          ),
      },
      {
        field: "lastUpdate",
        headerName: "Last Update",
        minWidth: 170,
        renderCell: (p) => (
          <Typography sx={{ color: "text.secondary", fontWeight: 700 }}>
            {p.value ? new Date(p.value).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "Action",
        sortable: false,
        filterable: false,
        width: 110,
        renderCell: (p) => (
          <Tooltip title="Remove from group">
            <span>
              <IconButton
                onClick={() => openRemove("device", p.row.raw)}
                disabled={isRemovingDevice}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  background: alpha(theme.palette.error.main, 0.08),
                }}
              >
                <Trash2 size={18} />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [navigate, theme, isRemovingDevice]
  );

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}
        >
          <Stack spacing={2}>
            <Skeleton width="55%" height={34} />
            <Skeleton variant="rounded" height={140} />
            <Skeleton variant="rounded" height={420} />
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (error || !group) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}
        >
          <Alert
            severity="error"
            action={
              <Button onClick={() => (isDemoLocalGroup ? navigate("/groups") : refetch())} size="small">
                Retry
              </Button>
            }
          >
            {error?.data?.msg || "Error loading group details"}
          </Alert>
        </Paper>
      </Box>
    );
  }

  const managers = group.managers?.filter((m) => m.role !== "owner") || [];
  const viewers = group.viewers || [];
  const childGroups = group.childGroups || [];
  const totalDevices = stats?.devices?.total || group.devices?.length || 0;
  const groupTypeLabel = (group.groupType || "fleet").replaceAll("_", " ");
  const cardSx = {
    borderRadius: "12px",
    border: "1px solid rgba(226,232,240,0.95)",
    boxShadow: "0 18px 44px rgba(15,23,42,0.06)",
    background: "#fff",
  };
  const iconTileSx = (color = "#6366f1") => ({
    width: 42,
    height: 42,
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    bgcolor: alpha(color, 0.12),
    color,
    flexShrink: 0,
  });
  const primaryActionSx = {
    height: 44,
    px: 2.1,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 900,
    color: "#fff",
    background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
    boxShadow: "0 14px 28px rgba(79,70,229,0.28)",
    "&:hover": {
      background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
      boxShadow: "0 16px 32px rgba(79,70,229,0.34)",
    },
  };
  const heroButtonSx = {
    height: 44,
    borderRadius: "10px",
    px: 1.8,
    color: "#fff",
    borderColor: "rgba(255,255,255,0.34)",
    bgcolor: "rgba(255,255,255,0.04)",
    textTransform: "none",
    fontWeight: 900,
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "rgba(255,255,255,0.58)",
      bgcolor: "rgba(255,255,255,0.1)",
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: "#f6f8fc",
        pb: { xs: 2, md: 3 },
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1480,
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 2.5, xl: 3 },
          py: { xs: 1.25, md: 2 },
        }}
      >
        <Button
          variant="text"
          onClick={() => navigate("/groups")}
          startIcon={<ArrowLeft size={17} />}
          sx={{
            mb: 1.5,
            px: 0,
            color: "#4f46e5",
            fontWeight: 950,
            textTransform: "none",
            "&:hover": { background: "transparent", color: "#4338ca" },
          }}
        >
          Back to Groups
        </Button>

        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: { xs: 2, md: 3 },
            borderRadius: "14px",
            overflow: "hidden",
            position: "relative",
            border: "1px solid rgba(129,140,248,0.18)",
            background:
              "radial-gradient(circle at 78% 25%, rgba(124,58,237,0.36), transparent 30%), radial-gradient(circle at 105% 110%, rgba(99,102,241,0.42), transparent 34%), linear-gradient(135deg, #0e1237 0%, #11103d 48%, #201255 100%)",
            boxShadow: "0 24px 60px rgba(30, 27, 75, 0.22)",
            "&:after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "repeating-linear-gradient(160deg, rgba(255,255,255,0.045) 0px, rgba(255,255,255,0.045) 1px, transparent 1px, transparent 12px)",
              opacity: 0.25,
              pointerEvents: "none",
            },
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", lg: "center" }}
            justifyContent="space-between"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: { xs: 58, md: 74 },
                  height: { xs: 58, md: 74 },
                  borderRadius: "13px",
                  display: "grid",
                  placeItems: "center",
                  background: "linear-gradient(135deg, #6d5dfc 0%, #4f46e5 100%)",
                  color: "#fff",
                  boxShadow: "0 18px 36px rgba(79,70,229,0.36)",
                  flexShrink: 0,
                }}
              >
                <Users size={isDownMd ? 26 : 32} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: { xs: 24, md: 30 },
                      lineHeight: 1.1,
                      fontWeight: 950,
                      maxWidth: { xs: "100%", md: 520 },
                      overflowWrap: "anywhere",
                    }}
                  >
                    {group.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={groupTypeLabel}
                    sx={{
                      height: 25,
                      borderRadius: "8px",
                      color: "#fff",
                      bgcolor: "rgba(99,102,241,0.86)",
                      fontWeight: 950,
                      textTransform: "capitalize",
                    }}
                  />
                </Stack>
                <Typography
                  sx={{
                    mt: 1,
                    color: "rgba(255,255,255,0.82)",
                    fontSize: { xs: 13, md: 14 },
                    fontWeight: 650,
                    maxWidth: 620,
                    overflowWrap: "anywhere",
                  }}
                >
                  {group.description || group.owner?.mobileNo || group.owner?.email || "No description available."}
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              justifyContent={{ xs: "stretch", lg: "flex-end" }}
              sx={{
                width: { xs: "100%", lg: "auto" },
                "& .MuiButton-root": { flex: { xs: "1 1 170px", sm: "0 0 auto" } },
              }}
            >
              <Button variant="outlined" startIcon={<Plus size={17} />} onClick={() => setIsDeviceModalOpen(true)} sx={heroButtonSx}>
                Add Device
              </Button>
              <Button variant="outlined" startIcon={<Eye size={17} />} onClick={() => setIsViewerModalOpen(true)} sx={heroButtonSx}>
                Add Viewer
              </Button>
              <Button variant="outlined" startIcon={<ShieldCheck size={17} />} onClick={() => setIsManagerModalOpen(true)} sx={heroButtonSx}>
                Add Manager
              </Button>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.34)",
                    bgcolor: "rgba(255,255,255,0.04)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <RefreshCw size={18} />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Edit2 size={17} />}
                onClick={() => navigate(`/groups/${groupId}/edit`)}
                sx={primaryActionSx}
              >
                Edit Group
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(12, minmax(0, 1fr))",
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ gridColumn: { lg: "span 2" } }}>
            <StatCard label="Total Devices" value={totalDevices} subValue={`${onlineCount} online`} icon={<Smartphone size={20} />} color="#3b82f6" />
          </Box>
          <Box sx={{ gridColumn: { lg: "span 2" } }}>
            <StatCard label="Managers" value={managers.length || 0} subValue={`${managers.length || 0} assigned`} icon={<ShieldCheck size={20} />} color="#7c3aed" />
          </Box>
          <Box sx={{ gridColumn: { lg: "span 2" } }}>
            <StatCard label="Viewers" value={viewers.length || 0} subValue={`${viewers.length || 0} assigned`} icon={<Eye size={20} />} color="#f97316" />
          </Box>
          <Box sx={{ gridColumn: { lg: "span 2" } }}>
            <StatCard label="Moving Now" value={stats?.devices?.moving || 0} subValue={`${idleCount} idle`} icon={<BarChart3 size={20} />} color="#16a34a" />
          </Box>
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              gridColumn: { xs: "span 1", sm: "span 2", lg: "span 4" },
              p: { xs: 2, md: 2.2 },
              minHeight: 152,
            }}
          >
            <Typography sx={{ color: "#0f172a", fontSize: 13, fontWeight: 950, mb: 2 }}>
              Device Status Overview
            </Typography>
            <Stack direction="row" spacing={2.2} alignItems="center" justifyContent="space-between">
              <Box
                sx={{
                  width: { xs: 104, sm: 128 },
                  height: { xs: 104, sm: 128 },
                  borderRadius: "50%",
                  background: `conic-gradient(#22c55e 0 ${(onlineCount / deviceStatusTotal) * 360}deg, #f59e0b ${(onlineCount / deviceStatusTotal) * 360}deg ${((onlineCount + idleCount) / deviceStatusTotal) * 360}deg, #ef4444 ${((onlineCount + idleCount) / deviceStatusTotal) * 360}deg 360deg)`,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ width: "70%", height: "70%", borderRadius: "50%", bgcolor: "#fff", display: "grid", placeItems: "center", textAlign: "center" }}>
                  <Box>
                    <Typography sx={{ fontSize: 25, lineHeight: 1, color: "#0f172a", fontWeight: 950 }}>{totalDevices}</Typography>
                    <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: 12, fontWeight: 800 }}>Total</Typography>
                  </Box>
                </Box>
              </Box>
              <Stack spacing={1.15} sx={{ flex: 1, minWidth: 0 }}>
                {[
                  { label: "Online", value: onlineCount, color: "#22c55e" },
                  { label: "Idle", value: idleCount, color: "#f59e0b" },
                  { label: "Offline", value: offlineCount, color: "#ef4444" },
                ].map((row) => (
                  <Stack key={row.label} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: row.color, flexShrink: 0 }} />
                      <Typography sx={{ color: "#334155", fontSize: 13, fontWeight: 800 }} noWrap>{row.label}</Typography>
                    </Stack>
                    <Typography sx={{ color: "#0f172a", fontSize: 13, fontWeight: 950 }} noWrap>
                      {row.value} ({Math.round((row.value / deviceStatusTotal) * 100)}%)
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Box>

        <Paper elevation={0} sx={{ ...cardSx, mb: 2, overflow: "hidden" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ p: { xs: 1.8, md: 2.4 } }}
          >
            <Stack direction="row" spacing={1.3} alignItems="center">
              <Box sx={iconTileSx("#6366f1")}>
                <Smartphone size={19} />
              </Box>
              <Box>
                <Typography sx={{ color: "#0f172a", fontSize: { xs: 18, md: 22 }, fontWeight: 950 }}>
                  Devices in this group
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
                  {deviceRows.length} total devices
                </Typography>
              </Box>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ xs: "stretch", sm: "center" }}>
              <TextField
                value={deviceSearch}
                onChange={(event) => setDeviceSearch(event.target.value)}
                placeholder="Search by device name, IMEI..."
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} color="#94a3b8" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 300 },
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                    borderRadius: "9px",
                    bgcolor: "#fff",
                    "& fieldset": { borderColor: "rgba(203,213,225,0.95)" },
                  },
                }}
              />
              <Button variant="outlined" startIcon={<SlidersHorizontal size={16} />} sx={{ height: 44, borderRadius: "9px", textTransform: "none", fontWeight: 900 }}>
                Filters
              </Button>
              <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setIsDeviceModalOpen(true)} sx={primaryActionSx}>
                Add Device
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ width: "100%", overflowX: "auto", borderTop: "1px solid rgba(226,232,240,0.92)" }}>
            <Box sx={{ minWidth: 760, height: filteredDeviceRows.length ? 462 : 420 }}>
              <DataGrid
                rows={filteredDeviceRows}
                columns={deviceCols}
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10, page: 0 } },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Stack alignItems="center" justifyContent="center" spacing={1.4} sx={{ height: "100%", textAlign: "center", px: 2 }}>
                      <Box
                        sx={{
                          width: 78,
                          height: 58,
                          borderRadius: "16px",
                          display: "grid",
                          placeItems: "center",
                          color: "#818cf8",
                          background: "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(129,140,248,0.2))",
                          boxShadow: "0 12px 30px rgba(99,102,241,0.18)",
                        }}
                      >
                        <Smartphone size={32} />
                      </Box>
                      <Typography sx={{ color: "#0f172a", fontSize: 18, fontWeight: 950 }}>
                        No devices found in this group
                      </Typography>
                      <Typography sx={{ color: "#64748b", fontSize: 14, fontWeight: 650 }}>
                        Add devices to start monitoring and tracking.
                      </Typography>
                      <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setIsDeviceModalOpen(true)} sx={{ ...primaryActionSx, mt: 1 }}>
                        Add Device
                      </Button>
                    </Stack>
                  ),
                }}
                sx={{
                  border: "none",
                  color: "#0f172a",
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: "#f8fafc",
                    borderBottom: "1px solid rgba(226,232,240,0.95)",
                    color: "#0f172a",
                    fontWeight: 950,
                  },
                  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 950 },
                  "& .MuiDataGrid-cell": {
                    borderColor: "rgba(226,232,240,0.84)",
                    outline: "none",
                  },
                  "& .MuiDataGrid-row:hover": { bgcolor: "rgba(99,102,241,0.04)" },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "1px solid rgba(226,232,240,0.92)",
                    bgcolor: "#fff",
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
            mb: 2,
          }}
        >
          {[
            {
              title: "Managers",
              count: managers.length,
              color: "#7c3aed",
              icon: <ShieldCheck size={19} />,
              emptyIcon: <Users size={33} />,
              emptyTitle: "No managers assigned yet.",
              emptyCopy: "Add managers to allow group management.",
              action: "Add Manager",
              view: "View all managers",
              onAction: () => setIsManagerModalOpen(true),
              items: managers,
              type: "manager",
            },
            {
              title: "Viewers",
              count: viewers.length,
              color: "#f97316",
              icon: <Eye size={19} />,
              emptyIcon: <Users size={33} />,
              emptyTitle: "No viewers assigned yet.",
              emptyCopy: "Add viewers to allow read-only access.",
              action: "Add Viewer",
              view: "View all viewers",
              onAction: () => setIsViewerModalOpen(true),
              items: viewers,
              type: "viewer",
            },
          ].map((section) => (
            <Paper key={section.title} elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 2.3 }, minHeight: 318 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box sx={iconTileSx(section.color)}>{section.icon}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: "#0f172a", fontSize: 18, fontWeight: 950 }} noWrap>
                      {section.title}
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
                      {section.count} assigned
                    </Typography>
                  </Box>
                </Stack>
                <Button variant="outlined" startIcon={<Plus size={15} />} onClick={section.onAction} sx={{ height: 38, borderRadius: "9px", textTransform: "none", fontWeight: 900 }}>
                  {section.action}
                </Button>
              </Stack>
              <Divider sx={{ my: 1.8 }} />
              {section.items.length ? (
                <Stack spacing={1}>
                  {section.items.map((entry) => {
                    const user = section.type === "manager" ? entry.user || entry : entry;
                    const userId = user?._id;
                    return (
                      <Paper
                        key={userId || user?.email || user?.username}
                        elevation={0}
                        sx={{
                          p: 1.25,
                          borderRadius: "10px",
                          border: "1px solid rgba(226,232,240,0.9)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(section.color, 0.82), fontWeight: 950 }}>
                            {user?.username?.charAt(0)?.toUpperCase() || (section.type === "manager" ? "M" : "V")}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: "#0f172a", fontWeight: 950 }} noWrap>
                              {user?.username || "Unknown"}
                            </Typography>
                            <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 700 }} noWrap>
                              {user?.email || "—"}
                            </Typography>
                          </Box>
                        </Stack>
                        <Tooltip title="Remove">
                          <span>
                            <IconButton
                              onClick={() => openRemove(section.type, userId)}
                              disabled={section.type === "manager" ? isRemovingManager : isRemovingViewer}
                              sx={{ borderRadius: "9px", color: "#ef4444", bgcolor: "rgba(239,68,68,0.08)" }}
                            >
                              <Trash2 size={17} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ minHeight: 185, textAlign: "center", px: 2 }}>
                  <Box sx={{ width: 64, height: 64, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: alpha(section.color, 0.12), color: section.color }}>
                    {section.emptyIcon}
                  </Box>
                  <Typography sx={{ color: "#0f172a", fontWeight: 950 }}>
                    {section.emptyTitle}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 650 }}>
                    {section.emptyCopy}
                  </Typography>
                  <Button variant="text" onClick={section.onAction} sx={{ color: section.color, fontWeight: 950, textTransform: "none" }}>
                    {section.view}
                  </Button>
                </Stack>
              )}
            </Paper>
          ))}
        </Box>

        {(group.owner || childGroups.length) ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: group.owner && childGroups.length ? "repeat(2, minmax(0, 1fr))" : "1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            {group.owner ? (
              <Paper elevation={0} sx={{ ...cardSx, p: 2.2 }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha(headerColor, 0.9), fontWeight: 950 }}>
                    {group.owner.username?.charAt(0)?.toUpperCase() || "O"}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: "#0f172a", fontWeight: 950 }} noWrap>Group Owner</Typography>
                    <Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 700 }} noWrap>
                      {group.owner.username || "Unknown"} · {group.owner.email || "—"}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ) : null}
            {childGroups.length ? (
              <Paper elevation={0} sx={{ ...cardSx, p: 2.2 }}>
                <Stack spacing={1.1}>
                  <Typography sx={{ color: "#0f172a", fontWeight: 950 }}>Sub-Groups ({childGroups.length})</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {childGroups.map((c) => (
                      <Chip
                        key={c._id}
                        label={c.name}
                        onClick={() => navigate(`/groups/${c._id}`)}
                        sx={{ borderRadius: "9px", fontWeight: 850, bgcolor: "#eef2ff", color: "#4338ca" }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            ) : null}
          </Box>
        ) : null}

        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 2.3 }, minHeight: 236 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={iconTileSx("#6366f1")}>
                <BarChart3 size={19} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: "#0f172a", fontSize: 18, fontWeight: 950 }} noWrap>
                  Recent Activity
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
                  Latest activities in this group
                </Typography>
              </Box>
            </Stack>
            <Button variant="outlined" endIcon={<SlidersHorizontal size={15} />} sx={{ height: 40, borderRadius: "9px", textTransform: "none", fontWeight: 900 }}>
              All Activity
            </Button>
          </Stack>
          <Stack alignItems="center" justifyContent="center" spacing={1.2} sx={{ minHeight: 145, textAlign: "center" }}>
            <Box sx={{ width: 58, height: 58, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "#eef2ff", color: "#64748b" }}>
              <CalendarDays size={26} />
            </Box>
            <Typography sx={{ color: "#0f172a", fontWeight: 950 }}>No recent activity</Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 650 }}>
              All activities will appear here.
            </Typography>
          </Stack>
        </Paper>
      </Box>

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirm.open}
        title="Remove from Group"
        desc="This action will unassign the selected item from this group. This can be added again later if needed."
        loading={confirmLoading}
        onClose={closeRemove}
        onConfirm={handleConfirmRemove}
      />

      {/* Modals */}
      <AddViewerModal
        isOpen={isViewerModalOpen}
        onClose={() => {
          setIsViewerModalOpen(false);
          refreshGroupDetail();
        }}
        groupId={groupId}
      />

      <AddDeviceToGroupModal
        isOpen={isDeviceModalOpen}
        onClose={() => {
          setIsDeviceModalOpen(false);
          refreshGroupDetail();
        }}
        groupId={groupId}
      />

      <AddManagerModal
        isOpen={isManagerModalOpen}
        onClose={() => {
          setIsManagerModalOpen(false);
          refreshGroupDetail();
        }}
        groupId={groupId}
      />
    </Box>
  );
}
